import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cn } from '@/utils';

const ContextMenuContext = React.createContext<{
  open: boolean;
  setOpen: (open: boolean) => void;
  position: { x: number; y: number };
  setPosition: (position: { x: number; y: number }) => void;
}>({
  open: false,
  setOpen: () => {},
  position: { x: 0, y: 0 },
  setPosition: () => {},
});

interface ContextMenuProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

const ContextMenu = ({ children, onOpenChange }: ContextMenuProps) => {
  const [open, setOpenState] = React.useState(false);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  
  const setOpen = React.useCallback((newOpen: boolean) => {
    setOpenState(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  }, [onOpenChange]);

  return (
    <ContextMenuContext.Provider value={{ open, setOpen, position, setPosition }}>
      {children}
    </ContextMenuContext.Provider>
  );
};
ContextMenu.displayName = 'ContextMenu';

interface ContextMenuTriggerProps {
  children: React.ReactNode;
}

const ContextMenuTrigger = React.forwardRef<HTMLDivElement, ContextMenuTriggerProps & React.HTMLAttributes<HTMLDivElement>>(
  ({ children, ...props }, ref) => {
    const context = React.useContext(ContextMenuContext);
    
    const handleContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      context.setPosition({ x: e.clientX, y: e.clientY });
      context.setOpen(true);
    };

    return (
      <div
        ref={ref}
        onContextMenu={handleContextMenu}
        {...props}
      >
        {children}
      </div>
    );
  }
);
ContextMenuTrigger.displayName = 'ContextMenuTrigger';

interface ContextMenuContentProps {
  className?: string;
  children: React.ReactNode;
}

const ContextMenuContent = React.forwardRef<HTMLDivElement, ContextMenuContentProps>(
  ({ className, children, ...props }, ref) => {
    const context = React.useContext(ContextMenuContext);
    
    React.useEffect(() => {
      const handleClickOutside = () => context.setOpen(false);
      if (context.open) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }, [context.open, context.setOpen]);
    
    if (!context.open) return null;
    
    return ReactDOM.createPortal(
      <div
        ref={ref}
        className={cn(
          'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className
        )}
        style={{
          position: 'fixed',
          left: context.position.x,
          top: context.position.y,
        }}
        {...props}
      >
        {children}
      </div>,
      document.body
    );
  }
);
ContextMenuContent.displayName = 'ContextMenuContent';

interface ContextMenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  inset?: boolean;
}

const ContextMenuItem = React.forwardRef<HTMLButtonElement, ContextMenuItemProps>(
  ({ className, inset, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground',
        inset && 'pl-8',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
ContextMenuItem.displayName = 'ContextMenuItem';

const ContextMenuSeparator = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
);
ContextMenuSeparator.displayName = 'ContextMenuSeparator';

export { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator };